import { useLingui } from '@lingui/react/macro';
import { toWeeklyBreakdownByTimeType } from '@sit/client-shared';
import { getStartOfWeek, getStringFromDate } from '@sit/core';
import { GetTimesheetsResponse, useGetTimesheet, useGetTimesheetRows } from '@sit/openapi';
import { skipToken } from '@tanstack/react-query';
import { usePostTimesheetSubmitAsync } from '@web/api/timesheet/use-post-timesheet-submit';
import { useShowOvertime } from '@web/api/users/use-show-overtime';
import { hasMissingRequiredDimensionsInRows } from '@web/helpers/timesheet';
import toastr from '@web/helpers/toastr';
import { useEmployeeOvertimeCalculations } from '@web/hooks/useOvertimeEmployeeCalculations';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { missingRequiredDimensions } from '../components/messages';

export type MinimalTimesheetForSubmit = Pick<GetTimesheetsResponse[number], 'id' | 'employeeId' | 'startDate' | 'weekStart' | 'companyId'>;

export const useOvertimeSubmitGuard = () => {
  const { i18n } = useLingui();
  const { mutate: submitTimesheetAsync } = usePostTimesheetSubmitAsync();
  const [pendingSubmit, setPendingSubmit] = useState<MinimalTimesheetForSubmit | null>(null);
  const [showOvertimeWarning, setShowOvertimeWarning] = useState(false);
  const { data: timesheet, error: timesheetError } = useGetTimesheet(pendingSubmit?.id);
  const { data: rows, error: rowsError } = useGetTimesheetRows(pendingSubmit ? { pathParams: { id: pendingSubmit.id } } : skipToken);

  const weekStartDate = useMemo(
    () => (pendingSubmit ? getStartOfWeek<string>(pendingSubmit.weekStart, getStringFromDate(pendingSubmit.startDate)) : undefined),
    [pendingSubmit],
  );

  const showOvertime = useShowOvertime({ timesheetId: pendingSubmit?.id });
  const { data: overtimeCalculations } = useEmployeeOvertimeCalculations({
    employeeId: pendingSubmit?.employeeId,
    weekStartDate,
    companyId: pendingSubmit?.companyId,
    timesheetId: pendingSubmit?.id,
  });

  const handleSubmitConfirm = useCallback(
    (overtimeComment?: string) => {
      if (!pendingSubmit) return;
      submitTimesheetAsync({
        pathParams: { id: pendingSubmit.id },
        body: {
          employeeOvertimeComment: overtimeComment || null,
        },
      });
      setShowOvertimeWarning(false);
      setPendingSubmit(null);
    },
    [pendingSubmit, submitTimesheetAsync],
  );

  const handleOvertimeWarningCancel = useCallback(() => {
    setShowOvertimeWarning(false);
    setPendingSubmit(null);
  }, []);

  useEffect(() => {
    if (!pendingSubmit) return;

    if (timesheetError || rowsError) {
      setPendingSubmit(null);
      return;
    }

    if (!timesheet || !rows) return;

    if (hasMissingRequiredDimensionsInRows(rows, timesheet)) {
      toastr.warning(i18n._(missingRequiredDimensions));
      setPendingSubmit(null);
      return;
    }

    if (!showOvertime) {
      handleSubmitConfirm();
      return;
    }

    if (overtimeCalculations) {
      const { hasDiscrepancies } = toWeeklyBreakdownByTimeType(overtimeCalculations.overtimeBreakdown || {});
      if (hasDiscrepancies) {
        setShowOvertimeWarning(true);
      } else {
        handleSubmitConfirm();
      }
    }
  }, [handleSubmitConfirm, i18n, pendingSubmit, rows, rowsError, showOvertime, timesheet, timesheetError, overtimeCalculations]);

  const requestSubmit = useCallback((timesheetToSubmit: MinimalTimesheetForSubmit) => {
    setPendingSubmit(timesheetToSubmit);
  }, []);

  const overtimeModal = {
    isOpen: showOvertimeWarning,
    overtimeCalculations,
    weekStartDate,
    onConfirm: handleSubmitConfirm,
    onCancel: handleOvertimeWarningCancel,
  } as const;

  return {
    requestSubmit,
    overtimeModal,
  } as const;
};
